import R from 'ramda';
import { validateOptions } from './optionsValidation';
import * as applicationActions from 'application/applicationActions';
import * as optionsActions from './optionsActions';
import * as submissionActions from 'submission/submissionActions';

import {
  completeStep,
  initialStepState,
  setCompleted,
  setErrors,
  setFieldValue,
  setFields,
  setLoadingLocalStorage,
} from 'common/stepReducerUtil';

export const EMBARGO_PERIOD_NONE = "Don't delay";
export const EMBARGO_PERIOD_SIX_MONTHS = '6 months';
export const EMBARGO_PERIOD_ONE_YEAR = '1 year';
export const EMBARGO_PERIOD_TWO_YEARS = '2 years';

const initialOptionsStepState = {
  loadingInitData: false,
  fields: {
    acceptedOptIns: [],
    declinedOptIns: [],
    embargoPeriod: EMBARGO_PERIOD_NONE,
  },
  optInTermsDialog: {
    loading: true,
    visible: false,
    optInId: '',
  },
  optIns: [],
};

const initialState = R.merge(initialStepState, initialOptionsStepState);

const optInsLens = R.lensProp('optIns');
const setOptIns = R.set(optInsLens);
export const getOptIns = R.view(optInsLens);

const optInTermsDialogLens = R.lensProp('optInTermsDialog');
export const getOptInTermsDialog = R.view(optInTermsDialogLens);

const optInTermsDialogLoadingLens = R.lensPath(['optInTermsDialog', 'loading']);
const setOptInTermsDialogLoading = R.set(optInTermsDialogLoadingLens);
export const getOptInTermsDialogLoading = R.view(optInTermsDialogLoadingLens);

const optInTermsDialogVisibleLens = R.lensPath(['optInTermsDialog', 'visible']);
const setOptInTermsDialogVisible = R.set(optInTermsDialogVisibleLens);
export const getOptInTermsDialogVisible = R.view(optInTermsDialogVisibleLens);

const optInTermsDialogOptInIdLens = R.lensPath(['optInTermsDialog', 'optInId']);
const setOptInTermsDialogOptInId = R.set(optInTermsDialogOptInIdLens);
export const getOptInTermsDialogOptInId = R.view(optInTermsDialogOptInIdLens);

export const getOptInForId = (optInId, optIns) =>
  R.find(R.propEq('optInId', optInId), optIns);

const acceptedOptInsLens = R.lensPath(['fields', 'acceptedOptIns']);
const addAcceptedOptIn = R.curry((id, state) =>
  R.over(acceptedOptInsLens, R.append(id), state),
);
const removeAcceptedOptIn = R.curry((id, state) =>
  R.over(acceptedOptInsLens, R.without([id]), state),
);
export const getAcceptedOptIns = R.view(acceptedOptInsLens);

const declinedOptInsLens = R.lensPath(['fields', 'declinedOptIns']);
const addDeclinedOptIn = R.curry((id, state) =>
  R.over(declinedOptInsLens, R.append(id), state),
);
const removeDeclinedOptIn = R.curry((id, state) =>
  R.over(declinedOptInsLens, R.without([id]), state),
);
export const getDeclinedOptIns = R.view(declinedOptInsLens);

const embargoPeriodChanged = (embargoPeriod, state) =>
  R.pipe(setCompleted(false), setFieldValue(['embargoPeriod'], embargoPeriod))(
    state,
  );

const localStorageLoaded = (data, state) =>
  R.pipe(
    setLoadingLocalStorage(false),
    setCompleted(R.isEmpty(data) ? initialState.completed : data.completed),
    setFields(R.isEmpty(data) ? initialState.fields : data.fields),
  )(state);

const acceptOptIn = (id, state) =>
  R.pipe(
    setCompleted(false),
    setOptInTermsDialogVisible(false),
    setOptInTermsDialogLoading(true),
    removeDeclinedOptIn(id),
    addAcceptedOptIn(id),
  )(state);

const declineOptIn = (id, state) =>
  R.pipe(
    setCompleted(false),
    setOptInTermsDialogVisible(false),
    setOptInTermsDialogLoading(true),
    removeAcceptedOptIn(id),
    addDeclinedOptIn(id),
  )(state);

const undoOptIn = (id, state) =>
  R.pipe(setCompleted(false), removeAcceptedOptIn(id), removeDeclinedOptIn(id))(
    state,
  );

const viewOptInTerms = (id, state) =>
  R.pipe(setOptInTermsDialogVisible(true), setOptInTermsDialogOptInId(id))(
    state,
  );

const cancelSubmission = R.pipe(
  setCompleted(initialState.completed),
  setErrors(initialState.errors),
  setFields(initialState.fields),
);

const appDataLoaded = (data, state) => {
  const optIns = R.path(['project', 'optIns'], data);
  return setOptIns(optIns || [], state);
};

const optionsReducer = (state = initialState, action) => {
  switch (action.type) {
    case optionsActions.ACCEPT_OPT_IN:
      return acceptOptIn(action.id, state);

    case optionsActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(validateOptions(getOptIns(state)), state);

    case optionsActions.DECLINE_OPT_IN:
      return declineOptIn(action.id, state);

    case optionsActions.EMBARGO_PERIOD_CHANGED:
      return embargoPeriodChanged(action.embargoPeriod, state);

    case optionsActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case optionsActions.SET_TERMS_LOADING:
      return setOptInTermsDialogLoading(action.loading, state);

    case optionsActions.UNDO_OPT_IN:
      return undoOptIn(action.id, state);

    case optionsActions.VIEW_OPT_IN_TERMS:
      return viewOptInTerms(action.id, state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case applicationActions.APP_DATA_LOADED:
      return appDataLoaded(action.data, state);

    default:
      return state;
  }
};

export default optionsReducer;



// WEBPACK FOOTER //
// ./src/options/optionsReducer.js